/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  input,
  OnInit,
  signal,
  TemplateRef,
  untracked,
  viewChild,
  WritableSignal,
} from '@angular/core';
import {
  ControlsOf,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  InputType,
  ModalRef,
  ModalService,
  RowValidationList,
  RowValidationMap,
} from '@vinnova/ui';
import { format, getYear } from 'date-fns';
import {
  EMPTY,
  filter,
  finalize,
  forkJoin,
  merge,
  Observable,
  skip,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { FormBudget } from '../../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../../core/models/form.model';
import { Worksite } from '../../../../core/models/question/question-worksite.model';
import { Question } from '../../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../../core/queries/renderer-forms.query';
import { RendererFormsRepository } from '../../../../core/repositories/renderer-forms.repository';
import { BaseWorksiteService } from '../../../../core/services/base-worksite.service';
import {
  BudgetService,
  TotalUpprapporteradeKostnaderPerYear,
} from '../../../../core/services/budget.service';
import { FormValidationService } from '../../../../core/services/form-validation.service';
import { QuestionValueChangeService } from '../../../../core/services/question-value-change.service';
import { ReportService } from '../../../../core/services/report.service';
import { BudgetNoBudgetForKostnadslag } from '../validators/budget-no-budget-for-kostnadslag-warning.validator';
import { BudgetNoCostsReportedWarning } from '../validators/budget-no-costs-reported-warning.validator';
@UntilDestroy()
@Component({
  selector: 'lib-question-upparbetade-kostnader',
  templateUrl: './upparbetade-kostnader.component.html',
  styleUrls: ['./upparbetade-kostnader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UpparbetadeKostnaderComponent implements OnInit {
  KOSTNADSSLAG = [
    {
      id: '1',
      translationKey: 'question_budget_salary_cost',
    },
    { id: '2', translationKey: 'question_budget_equipment_cost' },
    { id: '3', translationKey: 'question_budget_consultant_cost' },
    { id: '4', translationKey: 'question_budget_other_cost' },
    { id: '5', translationKey: 'question_budget_indirect_cost' },
  ];

  questionForm = input<FormGroup<ControlsOf<FormBudget>>>();
  repeaterGroupId = input<string>();
  formBuilder = input<boolean>();
  question = input<Question>();
  showAterbetalningskrav = input<boolean>();
  readOnly$: Observable<ReadonlyState>;
  formControls: Map<string, FormControl<string>>;
  InputType = InputType;
  isMobile: boolean;
  columns: WritableSignal<
    {
      id: string;
      title: string;
      titleRow2: string;
      readonly: boolean;
    }[]
  > = signal([]);
  browseLarge: WritableSignal<{ start: number; length: number }[]> = signal([]);
  browseMedium: WritableSignal<{ start: number; length: number }[]> = signal(
    [],
  );
  browseSmall: WritableSignal<{ start: number; length: number }[]> = signal([]);
  tierTwoColumnHeaders: WritableSignal<
    {
      title?: string;
      translationKey?: string;
      colspan: number;
    }[]
  > = signal([]);
  isOpen: Map<string, boolean>;
  isVisible: Map<string, boolean>;
  projektparter: WritableSignal<{
    koordinator: Worksite;
    projektparter: Worksite[];
  }> = signal(undefined);
  isLoadingProjektparter = signal(false);
  inaktivaParter: Set<string>;
  activeLang$: Observable<string>;
  checkValidation$: Observable<boolean>;
  checkValidationSharedProjekt = new Subject<boolean>();
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  kostnaderRowValidation: RowValidationMap;
  startDate: Date;
  endDate: Date;
  ackumulerat: Map<
    string,
    {
      rowId: string;
      columnId: string;
      value: string;
    }[]
  >;
  totalUpparbetadeKostnader: WritableSignal<
    {
      rowId: string;
      columnId: string;
      value: string;
    }[]
  > = signal([]);
  tableClosed: Map<string, boolean> = new Map<string, boolean>();
  aterbetalningskravTableValues: Map<string, (number | string)[]>;
  redanAterbetalt = signal(0);
  ejUtbetalt = signal(0);
  aterbetalningskrav = signal(0);
  aterbetalningskravOpen = false;
  aterbetalningskravBrowseIndex = 1;
  aterbetalningskravHeaders = [
    'question_budget_aterbetalningskrav_projektparter',
    'question_budget_aterbetalningskrav_max_bidrag',
    'question_budget_aterbetalningskrav_max_stodniva',
    'question_budget_aterbetalningskrav_budget',
    'question_budget_aterbetalningskrav_rapporterade_kostnader',
    'question_budget_aterbetalningskrav_slutligt_bidrag',
    'question_budget_aterbetalningskrav_aterkrav',
  ];

  aterbetalningskravModal = viewChild<TemplateRef<unknown>>(
    'aterbetalningskravExpandedModal',
  );
  aterbetalningskravModalRef: ModalRef;

  ReadonlyState = ReadonlyState;

  isSharedView: boolean;
  showYearly = signal<boolean>(false);

  showToggle = false;

  get value() {
    return this.questionForm().get('value');
  }

  constructor(
    private questionValueChangeService: QuestionValueChangeService,
    private formsQuery: RendererFormsQuery,
    private cdr: ChangeDetectorRef,
    private worksiteService: BaseWorksiteService,
    private translocoService: TranslocoService,
    private fb: FormBuilder,
    private formValidationService: FormValidationService,
    private reportService: ReportService,
    private budgetService: BudgetService,
    private modalService: ModalService,
    private formRepository: RendererFormsRepository,
  ) {
    effect(() => {
      const repeaterGroupId = this.repeaterGroupId();
      untracked(() => {
        if (this.valueChangeSubscription) {
          this.valueChangeSubscription.unsubscribe();
        }
        if (this.patchValueSubscription) {
          this.valueChangeSubscription.unsubscribe();
        }

        this.valueChangeSubscription = this.questionValueChangeService
          .valueChangeListener(
            this.value,
            {
              questionId: this.question().id,
              repeaterGroupId: repeaterGroupId,
            },
            this.question(),
          )
          .pipe(untilDestroyed(this))
          .subscribe(() => {
            this.reportService.resetHasNoReportedCostsValue();
            this.checkAndSetHasReportedCost(
              this.projektparter().koordinator.id,
              this.projektparter().koordinator.name,
            );

            for (const projektpart of this.projektparter().projektparter) {
              this.checkAndSetHasReportedCost(projektpart.id, projektpart.name);
            }
          });

        this.patchValueSubscription = this.questionValueChangeService
          .patchValueListener(this.question(), repeaterGroupId, this.value)
          .pipe(
            switchMap(() => {
              if (this.showYearly()) {
                this.setColumnsYearly();
              } else {
                this.setColumns();
              }
              return this.loadParter().pipe(
                finalize(() => this.isLoadingProjektparter.set(false)),
              );
            }),
            untilDestroyed(this),
          )
          .subscribe();
      });
    });
  }

  ngOnInit(): void {
    this.activeLang$ = this.translocoService.langChanges$;
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.isSharedView = this.formRepository.getIsSharedView();

    if (this.isSharedView) {
      this.showToggle = true;
    }

    this.formRepository.showYearly$
      .pipe(untilDestroyed(this))
      .subscribe((val) => {
        this.showYearly.set(val);
        if (this.projektparter()) {
          if (this.showYearly()) {
            this.setColumnsYearly();
            this.setTotalsYearly();
          } else {
            this.setColumns();
            this.setTotals();
          }
        }
      });

    this.questionValueChangeService.patchInitialValue(
      this.question().id,
      this.repeaterGroupId(),
      this.value,
      this.question(),
    );

    this.isOpen = new Map<string, boolean>();
    this.isVisible = new Map<string, boolean>();

    this.isMobile =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        navigator.userAgent,
      );
    if (this.showYearly()) {
      this.setColumnsYearly();
    } else {
      this.setColumns();
    }
    this.kostnaderRowValidation = new Map<string, RowValidationList>();
    this.loadParter()
      .pipe(
        untilDestroyed(this),
        finalize(() => this.isLoadingProjektparter.set(false)),
      )
      .subscribe(() => {
        this.createBudgetFormControls();
        this.checkAndSetHasReportedCost(
          this.projektparter().koordinator.id,
          this.projektparter().koordinator.name,
        );

        for (const projektpart of this.projektparter().projektparter) {
          this.checkAndSetHasReportedCost(projektpart.id, projektpart.name);
        }
      });

    this.checkValidation$ = merge(
      this.formValidationService.showErrors$,
      this.checkValidationSharedProjekt,
    ).pipe(
      filter((val) => val),
      take(1),
    );

    this.value.touch$.pipe(take(1)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  loadParter() {
    this.isLoadingProjektparter.set(true);
    const budget = this.budgetService.getUpparbetadeKostnaderBudget();
    if (!budget) {
      return EMPTY;
    }
    const projektparter = { koordinator: undefined, projektparter: [] };
    const worksitesObs: Observable<Worksite>[] = [];
    this.inaktivaParter = new Set<string>();
    for (const value of budget.projektparter) {
      worksitesObs.push(this.worksiteService.getWorksite(value.id));

      if (!value.aktiv) {
        this.inaktivaParter.add(value.id);
      }
    }

    return forkJoin(worksitesObs).pipe(
      tap((worksites) => {
        for (const value of budget.projektparter) {
          const worksite = worksites.find((w) => w.id === value.id);
          if (!worksite) {
            continue;
          }

          if (value.koordinerande) {
            projektparter.koordinator = worksite;
          } else {
            projektparter.projektparter.push(worksite);
          }
        }
        projektparter.projektparter.sort((a, b) => {
          const projektpartBudgetA = budget.projektparter.find(
            (p) => p.id === a.id,
          );
          const projektpartBudgetB = budget.projektparter.find(
            (p) => p.id === b.id,
          );
          if (projektpartBudgetA.aktiv && !projektpartBudgetB.aktiv) {
            return -1;
          } else if (!projektpartBudgetA.aktiv && projektpartBudgetB.aktiv) {
            return 1;
          } else {
            return a.company.name > b.company.name ? 1 : -1;
          }
        });
        this.projektparter.set(projektparter);
      }),
    );
  }

  private checkAndSetHasReportedCost(worksiteId: string, name: string) {
    const formControl = this.formControls.get(worksiteId);
    if (
      (formControl as any).warnings &&
      (formControl as any).warnings['BudgetNoCostsReportedWarning']
    ) {
      this.reportService.setHasNoReportedCostsValue(name);
    } else {
      if (this.reportService.getHasNoReportedCosts().includes(name)) {
        this.reportService.removeHasNoReportedCostsValue(name);
      }
    }
  }

  private setColumnsYearly() {
    const rapportBudgetDates = this.budgetService.getRapportBudgetDates();
    const upparbetadeKostnader =
      this.budgetService.getUpparbetadeKostnaderBudget();
    if (
      !rapportBudgetDates ||
      !rapportBudgetDates.ackumuleradeKostnaderEndDate ||
      !rapportBudgetDates.ackumuleradeKostnaderStartDate ||
      !rapportBudgetDates.projektEndDate ||
      !rapportBudgetDates.projektStartDate ||
      !rapportBudgetDates.redovisningsPerioder
    ) {
      return;
    }

    const columns = [];
    const colHead = [];

    for (const year of upparbetadeKostnader.projektparter[0]
      .upparbetadeKostnader[0].budgeterat) {
      columns.push(
        {
          id: 'summa_' + year.ar,
          title: year.ar,
          readonly: true,
        },
        {
          id: 'budgeterat_' + year.ar,
          title: year.ar,
          readonly: true,
        },
      );

      colHead.push(
        {
          translationKey: 'question_budget_actual',
          colspan: 1,
        },
        {
          translationKey: 'question_budget_actual_budget',
          colspan: 1,
        },
      );
    }

    columns.push(
      {
        id: 'kr',
        translationKey: 'question_budget_actual_sek',
        readonly: true,
      },
      {
        id: 'procent',
        title: '%',
        readonly: true,
      },
    );

    colHead.push({
      translationKey: this.isMobile
        ? 'question_budget_actual_remaining_mobile'
        : 'question_budget_actual_remaining',
      colspan: 2,
    });
    this.columns.set(columns);
    this.browseLarge.set(this.setBrowse(columns.length));
    this.browseMedium.set(this.setBrowse(columns.length, 'm'));
    this.browseSmall.set(this.setBrowse(columns.length, 's'));

    this.tierTwoColumnHeaders.set(colHead);
  }

  setBrowse(length, size?) {
    let width = 6;
    const increment = 2;
    if (size !== undefined) {
      width = 2;
    }

    const browse = [{ start: 0, length: width }];
    let i = increment;
    while (i + width <= length) {
      browse.push({ start: i, length: width });
      i += increment;
    }
    return browse;
  }

  private setColumns() {
    const rapportBudgetDates = this.budgetService.getRapportBudgetDates();
    const upparbetadeKostnader =
      this.budgetService.getUpparbetadeKostnaderBudget();
    if (
      !rapportBudgetDates ||
      !rapportBudgetDates.ackumuleradeKostnaderEndDate ||
      !rapportBudgetDates.ackumuleradeKostnaderStartDate ||
      !rapportBudgetDates.projektEndDate ||
      !rapportBudgetDates.projektStartDate ||
      !rapportBudgetDates.redovisningsPerioder
    ) {
      return;
    }
    const columns = [];
    const browseLarge = [
      {
        start: 0,
        length:
          this.isSharedView && this.showYearly()
            ? 6
            : 4 +
              (this.isSharedView
                ? 0
                : rapportBudgetDates.redovisningsPerioder.length),
      },
    ];
    const browseMedium = [];
    const browseSmall = [];
    if (!this.isSharedView) {
      for (const redovisningsPeriod of rapportBudgetDates.redovisningsPerioder) {
        columns.push({
          id: format(redovisningsPeriod.startDate, 'yyyy'),
          title: format(redovisningsPeriod.startDate, 'yyyy-MM-dd'),
          titleRow2: format(redovisningsPeriod.endDate, 'yyyy-MM-dd'),
          readonly: false,
        });
      }
    }

    if (rapportBudgetDates.redovisningsPerioder.length === 1) {
      browseMedium.push(
        {
          start: 0,
          length: 2,
        },
        {
          start: 2,
          length: 3,
        },
      );

      browseSmall.push(
        {
          start: 0,
          length: 1,
        },
        {
          start: 1,
          length: 1,
        },
        {
          start: 2,
          length: 1,
        },
        {
          start: 3,
          length: 1,
        },
        {
          start: 4,
          length: 1,
        },
      );
    } else if (this.isSharedView) {
      browseMedium.push({
        start: 0,
        length: 4,
      });
      browseSmall.push(
        {
          start: 0,
          length: 1,
        },
        {
          start: 1,
          length: 1,
        },
        {
          start: 2,
          length: 1,
        },
        {
          start: 3,
          length: 1,
        },
      );
    } else {
      browseMedium.push(
        {
          start: 0,
          length: 3,
        },
        {
          start: 3,
          length: 3,
        },
      );

      browseSmall.push(
        {
          start: 0,
          length: 1,
        },
        {
          start: 1,
          length: 1,
        },
        {
          start: 2,
          length: 1,
        },
        {
          start: 3,
          length: 1,
        },
        {
          start: 4,
          length: 1,
        },
        {
          start: 5,
          length: 1,
        },
      );
    }
    columns.push(
      {
        id: 'ackumulerat',
        title: format(
          this.isSharedView
            ? rapportBudgetDates.projektStartDate
            : rapportBudgetDates.ackumuleradeKostnaderStartDate,
          'yyyy-MM-dd',
        ),
        titleRow2: format(
          this.isSharedView
            ? upparbetadeKostnader.redovisningsperiodSlutDatum
            : rapportBudgetDates.ackumuleradeKostnaderEndDate,
          'yyyy-MM-dd',
        ),
        readonly: true,
      },
      {
        id: 'budgeterat',
        title: format(rapportBudgetDates.projektStartDate, 'yyyy-MM-dd'),
        titleRow2: format(rapportBudgetDates.projektEndDate, 'yyyy-MM-dd'),
        readonly: true,
      },
      {
        id: 'kr',
        translationKey: 'question_budget_actual_sek',
        readonly: true,
      },
      {
        id: 'procent',
        title: '%',
        readonly: true,
      },
    );
    this.columns.set(columns);
    this.browseLarge.set(browseLarge);
    this.browseMedium.set(browseMedium);
    this.browseSmall.set(browseSmall);
    const colHead = [];
    if (!this.isSharedView) {
      colHead.push({
        translationKey: 'question_budget_actual_costs',
        colspan: rapportBudgetDates.redovisningsPerioder.length,
      });
    }
    colHead.push(
      {
        translationKey: 'question_budget_actual_acc_costs',
        colspan: 1,
      },
      {
        translationKey: 'question_budget_actual_budget',
        colspan: 1,
      },
      {
        translationKey: this.isMobile
          ? 'question_budget_actual_remaining_mobile'
          : 'question_budget_actual_remaining',
        colspan: 2,
      },
    );
    this.tierTwoColumnHeaders.set(colHead);
  }

  createBudgetFormControls() {
    this.formControls = new Map<string, FormControl<string>>();
    this.ackumulerat = new Map<string, []>();
    this.aterbetalningskravTableValues = new Map<string, (number | string)[]>();
    this.questionForm().setControl('tables', new FormArray([]));

    this.isOpen = new Map<string, boolean>();
    this.isVisible = new Map<string, boolean>();

    this.createControlsForProjektpart(this.projektparter().koordinator);
    for (const projektpart of this.projektparter().projektparter) {
      this.createControlsForProjektpart(projektpart);
    }

    if (this.showYearly()) {
      this.setTotalsYearly();
    } else {
      this.setTotals();
    }
    this.setAterbetalningskrav();
    this.cdr.markForCheck();
  }

  private createControlsForProjektpart(projektpart: Worksite) {
    this.isOpen.set(projektpart.id, !this.isMobile);
    this.isVisible.set(projektpart.id, !this.isMobile);

    const parsedValue = this.value.value
      ? JSON.parse(this.value.value)
      : undefined;
    const projektpartValue = parsedValue?.projektparter?.find(
      (p) => p.id === projektpart.id,
    );

    const rowValidationList = this.KOSTNADSSLAG.map((kostnad) => ({
      rowId: kostnad.id,
      validation: 'BudgetNoBudgetForKostnadslag',
    }));
    this.kostnaderRowValidation.set(projektpart.id, rowValidationList);

    const upparbetadeKostnaderValue = projektpartValue?.upparbetadeKostnader
      ? this.getUpparbetadeKostnaderValueForWorksite(projektpartValue)
      : undefined;

    const formControl = this.fb.control<string>(upparbetadeKostnaderValue);
    formControl.valueChanges
      .pipe(skip(1), untilDestroyed(this))
      .subscribe(() =>
        this.showYearly()
          ? this.setAckumuleratYearly(projektpart)
          : this.setAckumulerat(projektpart),
      );
    this.setValidation(formControl, projektpart.id);
    this.formControls.set(projektpart.id, formControl);
    this.questionForm().get('tables').push(formControl);
  }

  private getUpparbetadeKostnaderValueForWorksite(projektpartValue) {
    const upparbetadeKostnaderValue = [];

    for (const kostnadsslag of projektpartValue.upparbetadeKostnader) {
      for (const year of kostnadsslag.upparbetadeKostnaderPerAr) {
        upparbetadeKostnaderValue.push({
          rowId: kostnadsslag.kostnadsslag.toString(),
          columnId: year.ar.toString(),
          value: year.summa,
        });
      }
    }

    return JSON.stringify(upparbetadeKostnaderValue);
  }

  toggleCollapseAll(open: boolean) {
    for (const isOpenItem of this.isOpen) {
      if (this.projektparter().koordinator.id === isOpenItem[0]) {
        continue;
      }
      this.isOpen.set(isOpenItem[0], open);
    }
  }

  roundAterbetalningskrav() {
    return Math.round(this.aterbetalningskrav());
  }

  updateBudgetTables(save: boolean, worksiteId: string) {
    if (save) {
      this.setValue();
      if (this.showYearly()) {
        this.setTotalsYearly();
      } else {
        this.setTotals();
      }
      this.setAterbetalningskrav();
    }
    this.tableClosed.set(worksiteId, true);
  }

  private setTotalsYearly() {
    this.setAckumuleratYearly(this.projektparter().koordinator);
    for (const projektpart of this.projektparter().projektparter) {
      this.setAckumuleratYearly(projektpart);
    }

    const budget = this.budgetService.getUpparbetadeKostnaderBudget();
    const newTotalUpparbetadeKostnader = [];
    const newTotalUpparbetadeKostnaderYearly: TotalUpprapporteradeKostnaderPerYear[] =
      [
        { kostnadsslag: this.KOSTNADSSLAG[0].id, summaPerYear: [] },
        { kostnadsslag: this.KOSTNADSSLAG[1].id, summaPerYear: [] },
        { kostnadsslag: this.KOSTNADSSLAG[2].id, summaPerYear: [] },
        { kostnadsslag: this.KOSTNADSSLAG[3].id, summaPerYear: [] },
        { kostnadsslag: this.KOSTNADSSLAG[4].id, summaPerYear: [] },
      ];

    for (const formControl of this.formControls.entries()) {
      const parsed = (formControl[1] as FormControl<string>).value
        ? JSON.parse((formControl[1] as FormControl<string>).value)
        : [];

      for (const tableData of parsed) {
        const kostnadItem = newTotalUpparbetadeKostnader.find(
          (t) =>
            t.rowId === tableData.rowId && t.columnId === tableData.columnId,
        );
        if (kostnadItem) {
          kostnadItem.value = kostnadItem.value + tableData.value;
        }
      }
    }

    let sumAckumulerat = 0;

    for (const projektpart of budget.projektparter) {
      for (const upparbetadeKostnader of projektpart.upparbetadeKostnader) {
        for (const year of upparbetadeKostnader.upparbetadeKostnaderPerAr) {
          const kostnadsslag = newTotalUpparbetadeKostnaderYearly.find(
            (u) =>
              u.kostnadsslag === upparbetadeKostnader.kostnadsslag.toString(),
          );

          if (!kostnadsslag.summaPerYear.some((s) => s.year === year.ar))
            kostnadsslag.summaPerYear.push({
              year: year.ar,
              kostnad: year.summa,
              budget: 0,
            });
          else if (kostnadsslag.summaPerYear.some((s) => s.year === year.ar)) {
            kostnadsslag.summaPerYear.find((s) => s.year === year.ar).kostnad +=
              year.summa;
          }
        }
        for (const year of upparbetadeKostnader.budgeterat) {
          const kostnadsslag = newTotalUpparbetadeKostnaderYearly.find(
            (u) =>
              u.kostnadsslag === upparbetadeKostnader.kostnadsslag.toString(),
          );
          if (kostnadsslag.summaPerYear.some((s) => s.year === year.ar)) {
            kostnadsslag.summaPerYear.find((s) => s.year === year.ar).budget +=
              year.summa;
          }
        }
      }
    }

    for (const kostnadsslag of this.KOSTNADSSLAG) {
      const totalBudgetKostnadsslag = budget.totaltAckumuleradeKostnader.find(
        (kostnad) => kostnad.kostnadsslag.toString() === kostnadsslag.id,
      );
      const newAckumulerat = totalBudgetKostnadsslag.ackumulerat;
      sumAckumulerat += newAckumulerat;
      const krLeft = budget.isBudgetStandard
        ? undefined
        : totalBudgetKostnadsslag.budgeterat - newAckumulerat;
      const percentLeft = budget.isBudgetStandard
        ? undefined
        : 100 * (krLeft / totalBudgetKostnadsslag.budgeterat);
      for (const i of newTotalUpparbetadeKostnaderYearly.find(
        (k) => k.kostnadsslag.toString() === kostnadsslag.id,
      ).summaPerYear) {
        newTotalUpparbetadeKostnader.push(
          {
            rowId: kostnadsslag.id,
            columnId: 'summa_' + i.year,
            value: i.kostnad.toString(),
          },
          {
            rowId: kostnadsslag.id,
            columnId: 'budgeterat_' + i.year,
            value: i.budget.toString(),
          },
        );
      }

      newTotalUpparbetadeKostnader.push(
        {
          rowId: kostnadsslag.id,
          columnId: 'kr',
          value: budget.isBudgetStandard ? '' : krLeft.toString(),
        },
        {
          rowId: kostnadsslag.id,
          columnId: 'procent',
          value: budget.isBudgetStandard
            ? ''
            : totalBudgetKostnadsslag.budgeterat > 0
              ? percentLeft.toFixed(1)
              : '0.0',
        },
      );
    }

    if (budget.isBudgetStandard) {
      newTotalUpparbetadeKostnader.push(
        {
          rowId: 'sum',
          columnId: 'budgeterat',
          value: budget.summaBudgeteradeKostnader,
        },
        {
          rowId: 'sum',
          columnId: 'kr',
          value: budget.summaBudgeteradeKostnader - sumAckumulerat,
        },
      );
    }

    newTotalUpparbetadeKostnader.push({
      rowId: 'sum',
      columnId: 'procent',
      value:
        budget.summaBudgeteradeKostnader > 0
          ? (
              (100 * (budget.summaBudgeteradeKostnader - sumAckumulerat)) /
              budget.summaBudgeteradeKostnader
            ).toFixed(1)
          : '0.0',
    });

    this.totalUpparbetadeKostnader.set(newTotalUpparbetadeKostnader);
  }

  private setTotals() {
    this.setAckumulerat(this.projektparter().koordinator);
    for (const projektpart of this.projektparter().projektparter) {
      this.setAckumulerat(projektpart);
    }

    const budget = this.budgetService.getUpparbetadeKostnaderBudget();
    const newTotalUpparbetadeKostnader = [];

    const years = this.columns().filter((c) => !c.readonly);
    for (const kostnadsslag of this.KOSTNADSSLAG) {
      for (const year of years) {
        newTotalUpparbetadeKostnader.push({
          rowId: kostnadsslag.id,
          columnId: year.id,
          value: 0,
        });
      }
    }

    for (const formControl of this.formControls.entries()) {
      const parsed = (formControl[1] as FormControl<string>).value
        ? JSON.parse((formControl[1] as FormControl<string>).value)
        : [];

      for (const tableData of parsed) {
        const kostnadItem = newTotalUpparbetadeKostnader.find(
          (t) =>
            t.rowId === tableData.rowId && t.columnId === tableData.columnId,
        );
        if (kostnadItem) {
          kostnadItem.value = kostnadItem.value + tableData.value;
        }
      }
    }

    let sumAckumulerat = 0;

    for (const kostnadsslag of this.KOSTNADSSLAG) {
      let sumUpparbetadeKostnader = 0;
      for (const year of years) {
        const upparbetatYear =
          newTotalUpparbetadeKostnader.find(
            (k) => k.rowId === kostnadsslag.id && k.columnId === year.id,
          )?.value ?? 0;
        sumUpparbetadeKostnader += upparbetatYear;
      }

      const totalBudgetKostnadsslag = budget.totaltAckumuleradeKostnader.find(
        (kostnad) => kostnad.kostnadsslag.toString() === kostnadsslag.id,
      );
      const newAckumulerat =
        totalBudgetKostnadsslag.ackumulerat + sumUpparbetadeKostnader;
      sumAckumulerat += newAckumulerat;
      const krLeft = budget.isBudgetStandard
        ? undefined
        : totalBudgetKostnadsslag.budgeterat - newAckumulerat;
      const percentLeft = budget.isBudgetStandard
        ? undefined
        : 100 * (krLeft / totalBudgetKostnadsslag.budgeterat);
      newTotalUpparbetadeKostnader.push(
        {
          rowId: kostnadsslag.id,
          columnId: 'ackumulerat',
          value: newAckumulerat.toString(),
        },
        {
          rowId: kostnadsslag.id,
          columnId: 'budgeterat',
          value: budget.isBudgetStandard
            ? ''
            : totalBudgetKostnadsslag.budgeterat.toString(),
        },
        {
          rowId: kostnadsslag.id,
          columnId: 'kr',
          value: budget.isBudgetStandard ? '' : krLeft.toString(),
        },
        {
          rowId: kostnadsslag.id,
          columnId: 'procent',
          value: budget.isBudgetStandard
            ? ''
            : totalBudgetKostnadsslag.budgeterat > 0
              ? percentLeft.toFixed(1)
              : '0.0',
        },
      );
    }

    if (budget.isBudgetStandard) {
      newTotalUpparbetadeKostnader.push(
        {
          rowId: 'sum',
          columnId: 'budgeterat',
          value: budget.summaBudgeteradeKostnader,
        },
        {
          rowId: 'sum',
          columnId: 'kr',
          value: budget.summaBudgeteradeKostnader - sumAckumulerat,
        },
      );
    }

    newTotalUpparbetadeKostnader.push({
      rowId: 'sum',
      columnId: 'procent',
      value:
        budget.summaBudgeteradeKostnader > 0
          ? (
              (100 * (budget.summaBudgeteradeKostnader - sumAckumulerat)) /
              budget.summaBudgeteradeKostnader
            ).toFixed(1)
          : '0.0',
    });

    this.totalUpparbetadeKostnader.set(newTotalUpparbetadeKostnader);
  }

  private setAckumuleratYearly(projektpart: Worksite) {
    const formControl = this.formControls.get(projektpart.id);
    if (!formControl) {
      return;
    }

    const budget = this.budgetService.getUpparbetadeKostnaderBudget();
    const projektpartBudget = budget.projektparter.find(
      (p) => p.id === projektpart.id,
    );
    const upparbetadeKostnader = formControl.value
      ? JSON.parse(formControl.value)
      : [];
    const ackumuleratReadOnly = [];
    const years = this.columns().filter((c) => !c.readonly);
    let sumAckumulerat = 0;

    for (const kostnadsslag of this.KOSTNADSSLAG) {
      const ackumulerat = projektpartBudget.ackumuleradeKostnader.find(
        (ak) => ak.kostnadsslag.toString() === kostnadsslag.id,
      );
      let sumUpparbetadeKostnader = 0;
      for (const year of years) {
        const upparbetatYear =
          upparbetadeKostnader.find(
            (k) => k.rowId === kostnadsslag.id && k.columnId === year.id,
          )?.value ?? 0;
        sumUpparbetadeKostnader += upparbetatYear;
      }

      const newAckumulerat = ackumulerat.ackumulerat + sumUpparbetadeKostnader;
      sumAckumulerat += newAckumulerat;
      const krLeft = budget.isBudgetStandard
        ? undefined
        : ackumulerat.budgeterat - newAckumulerat;
      const percentLeft = budget.isBudgetStandard
        ? undefined
        : 100 * (krLeft / ackumulerat.budgeterat);

      for (const i of projektpartBudget.upparbetadeKostnader.find(
        (k) => k.kostnadsslag.toString() === kostnadsslag.id,
      ).upparbetadeKostnaderPerAr) {
        ackumuleratReadOnly.push({
          rowId: kostnadsslag.id,
          columnId: 'summa_' + i.ar,
          value: i.summa.toString(),
        });
      }
      for (const i of projektpartBudget.upparbetadeKostnader.find(
        (k) => k.kostnadsslag.toString() === kostnadsslag.id,
      ).budgeterat) {
        ackumuleratReadOnly.push({
          rowId: kostnadsslag.id,
          columnId: 'budgeterat_' + i.ar,
          value: i.summa.toString(),
        });
      }

      ackumuleratReadOnly.push(
        {
          rowId: kostnadsslag.id,
          columnId: 'kr',
          value: budget.isBudgetStandard ? '' : krLeft.toString(),
        },
        {
          rowId: kostnadsslag.id,
          columnId: 'procent',
          value: budget.isBudgetStandard
            ? ''
            : ackumulerat.budgeterat > 0
              ? percentLeft.toFixed(1)
              : '0.0',
        },
      );
    }
    if (budget.isBudgetStandard) {
      ackumuleratReadOnly.push(
        {
          rowId: 'sum',
          columnId: 'budgeterat',
          value: projektpartBudget.summaBudgeteradeKostnader,
        },
        {
          rowId: 'sum',
          columnId: 'kr',
          value: projektpartBudget.summaBudgeteradeKostnader - sumAckumulerat,
        },
      );
    }

    ackumuleratReadOnly.push({
      rowId: 'sum',
      columnId: 'procent',
      value:
        projektpartBudget.summaBudgeteradeKostnader > 0
          ? (
              (100 *
                (projektpartBudget.summaBudgeteradeKostnader -
                  sumAckumulerat)) /
              projektpartBudget.summaBudgeteradeKostnader
            ).toFixed(1)
          : '0.0',
    });

    this.ackumulerat.set(projektpart.id, ackumuleratReadOnly);
  }

  private setAckumulerat(projektpart: Worksite) {
    const formControl = this.formControls.get(projektpart.id);
    if (!formControl) {
      return;
    }

    const budget = this.budgetService.getUpparbetadeKostnaderBudget();
    const projektpartBudget = budget.projektparter.find(
      (p) => p.id === projektpart.id,
    );
    const upparbetadeKostnader = formControl.value
      ? JSON.parse(formControl.value)
      : [];
    const ackumuleratReadOnly = [];
    const years = this.columns().filter((c) => !c.readonly);
    let sumAckumulerat = 0;

    for (const kostnadsslag of this.KOSTNADSSLAG) {
      const ackumulerat = projektpartBudget.ackumuleradeKostnader.find(
        (ak) => ak.kostnadsslag.toString() === kostnadsslag.id,
      );
      let sumUpparbetadeKostnader = 0;
      for (const year of years) {
        const upparbetatYear =
          upparbetadeKostnader.find(
            (k) => k.rowId === kostnadsslag.id && k.columnId === year.id,
          )?.value ?? 0;
        sumUpparbetadeKostnader += upparbetatYear;
      }

      const newAckumulerat = ackumulerat.ackumulerat + sumUpparbetadeKostnader;
      sumAckumulerat += newAckumulerat;
      const krLeft = budget.isBudgetStandard
        ? undefined
        : ackumulerat.budgeterat - newAckumulerat;
      const percentLeft = budget.isBudgetStandard
        ? undefined
        : 100 * (krLeft / ackumulerat.budgeterat);
      ackumuleratReadOnly.push(
        {
          rowId: kostnadsslag.id,
          columnId: 'ackumulerat',
          value: newAckumulerat.toString(),
        },
        {
          rowId: kostnadsslag.id,
          columnId: 'budgeterat',
          value: budget.isBudgetStandard
            ? ''
            : ackumulerat.budgeterat.toString(),
        },
        {
          rowId: kostnadsslag.id,
          columnId: 'kr',
          value: budget.isBudgetStandard ? '' : krLeft.toString(),
        },
        {
          rowId: kostnadsslag.id,
          columnId: 'procent',
          value: budget.isBudgetStandard
            ? ''
            : ackumulerat.budgeterat > 0
              ? percentLeft.toFixed(1)
              : '0.0',
        },
      );
    }

    if (budget.isBudgetStandard) {
      ackumuleratReadOnly.push(
        {
          rowId: 'sum',
          columnId: 'budgeterat',
          value: projektpartBudget.summaBudgeteradeKostnader,
        },
        {
          rowId: 'sum',
          columnId: 'kr',
          value: projektpartBudget.summaBudgeteradeKostnader - sumAckumulerat,
        },
      );
    }

    ackumuleratReadOnly.push({
      rowId: 'sum',
      columnId: 'procent',
      value:
        projektpartBudget.summaBudgeteradeKostnader > 0
          ? (
              (100 *
                (projektpartBudget.summaBudgeteradeKostnader -
                  sumAckumulerat)) /
              projektpartBudget.summaBudgeteradeKostnader
            ).toFixed(1)
          : '0.0',
    });

    this.ackumulerat.set(projektpart.id, ackumuleratReadOnly);
  }

  private setAterbetalningskrav() {
    this.setAterbetalningskravForProjektpart(
      this.projektparter().koordinator.id,
    );
    for (const projektpart of this.projektparter().projektparter) {
      this.setAterbetalningskravForProjektpart(projektpart.id);
    }

    const budget = this.budgetService.getUpparbetadeKostnaderBudget();
    const aterbetalningskravProjektparter = [
      ...this.aterbetalningskravTableValues,
    ]
      .filter((b) => b[0] !== 'total')
      .map((b) => b[1]);

    const sumRapporteradeKostnader = aterbetalningskravProjektparter.reduce(
      (accumulator, currentValue) => accumulator + (currentValue[3] as number),
      0,
    );
    const sumSlutligtBidrag = aterbetalningskravProjektparter.reduce(
      (accumulator, currentValue) => accumulator + (currentValue[4] as number),
      0,
    );
    const sumAterbetalningskrav = aterbetalningskravProjektparter.reduce(
      (accumulator, currentValue) => accumulator + (currentValue[5] as number),
      0,
    );

    const aterbetalningskravTotalTableValues = [
      budget.maxBidrag,
      '-',
      budget.summaBudgeteradeKostnader,
      sumRapporteradeKostnader,
      sumSlutligtBidrag,
      sumAterbetalningskrav,
    ];

    this.aterbetalningskravTableValues.set(
      'total',
      aterbetalningskravTotalTableValues,
    );

    this.redanAterbetalt.set(budget.redanAterbetaltBelopp);
    this.ejUtbetalt.set(budget.ejUtbetaltBelopp);
    this.aterbetalningskrav.set(
      sumAterbetalningskrav -
        budget.redanAterbetaltBelopp -
        budget.ejUtbetaltBelopp,
    );
  }

  private setAterbetalningskravForProjektpart(projektpartId: string) {
    const budget = this.budgetService.getUpparbetadeKostnaderBudget();
    const budgetForProjektpart = budget.projektparter.find(
      (p) => p.id === projektpartId,
    );

    const ackumuleratValues = this.ackumulerat.get(projektpartId);
    const ackumuleratSum = ackumuleratValues
      .filter((a) => a.columnId === 'ackumulerat')
      .map((a) => parseInt(a.value))
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

    const rapporteradeKostnader = ackumuleratSum;
    const slutligtBidrag = Math.min(
      Math.ceil(
        (budgetForProjektpart.maxStodniva * rapporteradeKostnader) / 100,
      ),
      budgetForProjektpart.maxBidrag,
    );
    const aterbetalningsKrav = budgetForProjektpart.maxBidrag - slutligtBidrag;

    const aterbetalningskravTableValues = [
      budgetForProjektpart.maxBidrag,
      budgetForProjektpart.maxStodniva + '%',
      budgetForProjektpart.summaBudgeteradeKostnader,
      rapporteradeKostnader,
      slutligtBidrag,
      aterbetalningsKrav,
    ];

    this.aterbetalningskravTableValues.set(
      projektpartId,
      aterbetalningskravTableValues,
    );
  }

  private setValue() {
    const jsonValue = {} as any;
    jsonValue.projektparter = [];
    jsonValue.projektparter.push(
      this.createProjektpartJsonValue(this.projektparter().koordinator),
    );
    for (const part of this.projektparter().projektparter) {
      jsonValue.projektparter.push(this.createProjektpartJsonValue(part));
    }

    this.questionForm()
      .get('value')
      .patchValue(jsonValue ? JSON.stringify(jsonValue) : jsonValue);
    this.questionForm().markAsTouched();
    this.questionForm().get('value').markAsDirty();

    this.formValidationService.triggerSectionInvalidCheck(this.question().id);
  }

  private createProjektpartJsonValue(projektpart: Worksite) {
    const projektpartJsonValue = {} as any;
    projektpartJsonValue.id = projektpart.id;
    projektpartJsonValue.upparbetadeKostnader = [];

    const upparbetadeKostnader = this.formControls.get(projektpart.id).value
      ? JSON.parse(this.formControls.get(projektpart.id).value)
      : [];

    for (const kostnadsslag of this.KOSTNADSSLAG) {
      const kostnadsslagJsonValue = {} as any;
      kostnadsslagJsonValue.kostnadsslag = +kostnadsslag.id;
      kostnadsslagJsonValue.upparbetadeKostnaderPerAr = [];
      for (const year of this.columns().filter((c) => !c.readonly)) {
        const kostnadsslagYearJsonValue = {} as any;
        const upparbetadKostnad =
          upparbetadeKostnader.find(
            (k) => k.rowId === kostnadsslag.id && k.columnId === year.id,
          )?.value ?? 0;
        kostnadsslagYearJsonValue.ar = getYear(year.id);
        kostnadsslagYearJsonValue.summa = upparbetadKostnad;
        kostnadsslagJsonValue.upparbetadeKostnaderPerAr.push(
          kostnadsslagYearJsonValue,
        );
      }
      projektpartJsonValue.upparbetadeKostnader.push(kostnadsslagJsonValue);
    }
    return projektpartJsonValue;
  }

  private zeroBudgetKostadslagar(
    projektpartId,
  ): { id: string; budget: number }[] {
    const budgets =
      this.budgetService.getBudgeteratForProjektpart(projektpartId) ?? [];
    return budgets.filter((b) => b.budget === 0);
  }

  private setValidation(control: FormControl<string>, projektpartId: string) {
    control.clearValidators();
    control.addValidators(BudgetNoCostsReportedWarning);
    control.addValidators(
      BudgetNoBudgetForKostnadslag(this.zeroBudgetKostadslagar(projektpartId)),
    );

    control.updateValueAndValidity();
  }

  openAterbetalnignskravModal() {
    this.aterbetalningskravModalRef = this.modalService.show(
      this.aterbetalningskravModal(),
      { backdrop: 'static' },
    );
  }

  toggle(event) {
    this.formRepository.setShowYearly(event);
  }
}
